// src/components/store/sections/product-cards/TicketStubProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, Ticket, ShoppingCart, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const TICKET_THEMES = [
  { headerBg: "from-rose-500 to-orange-500", textAccent: "text-rose-600", btnBg: "bg-rose-600 hover:bg-rose-700" },
  { headerBg: "from-violet-600 to-indigo-600", textAccent: "text-violet-600", btnBg: "bg-violet-600 hover:bg-violet-700" },
  { headerBg: "from-emerald-500 to-teal-600", textAccent: "text-emerald-600", btnBg: "bg-emerald-600 hover:bg-emerald-700" },
  { headerBg: "from-sky-500 to-blue-600", textAccent: "text-blue-600", btnBg: "bg-blue-600 hover:bg-blue-700" },
];

export default function TicketStubProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    discountPercent,
    added,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = TICKET_THEMES[index % TICKET_THEMES.length];

  return (
    <div className="relative w-full h-full bg-white rounded-3xl border-2 border-slate-200/80 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col justify-between group select-none">
      {/* Top Ticket Header Strip */}
      <div>
        <div className={`px-4 py-2 bg-gradient-to-r ${theme.headerBg} text-white flex items-center justify-between text-[10px] font-mono font-black uppercase tracking-widest`}>
          <span className="flex items-center gap-1">
            <Ticket className="w-3.5 h-3.5" /> PASS #{String(index + 101)}
          </span>
          <span>SAVE {discountPercent}%</span>
        </div>

        {/* Product Image & Info */}
        <div className="p-4 pb-3">
          <Link href={productUrl} className="relative w-full h-40 rounded-2xl bg-slate-50 flex items-center justify-center p-2 mb-3 block">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
            <span className="truncate max-w-[120px] uppercase">{categoryName}</span>
            <span className="flex items-center gap-0.5 text-amber-500">
              <Star className="w-3 h-3 fill-amber-400" /> {rating.toFixed(1)}
            </span>
          </div>

          <Link href={productUrl} className="block mt-0.5">
            <h3 className="text-sm font-black text-slate-900 truncate hover:underline">
              {productName}
            </h3>
          </Link>
        </div>
      </div>

      {/* Perforated Tear-Off Divider with Side Cutouts */}
      <div className="relative flex items-center justify-between my-1">
        <div className="w-5 h-5 rounded-full bg-slate-100 border-r-2 border-slate-200 -ml-2.5 shrink-0" />
        <div className="w-full border-t-2 border-dashed border-slate-200 mx-1" />
        <div className="w-5 h-5 rounded-full bg-slate-100 border-l-2 border-slate-200 -mr-2.5 shrink-0" />
      </div>

      {/* Bottom Ticket Stub with Price & Barcode */}
      <div className="p-4 pt-2 flex items-center justify-between gap-2 bg-slate-50/50">
        <div>
          <span className="text-[9px] font-mono uppercase text-slate-400 block">ADMIT PRICE</span>
          <span className={`text-lg font-black font-mono ${theme.textAccent}`}>
            ৳{currentPrice.toLocaleString()}
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`px-4 py-2 rounded-xl ${theme.btnBg} text-white font-black text-[10px] uppercase tracking-wider shadow-sm active:scale-95 transition-all cursor-pointer flex items-center gap-1.5`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          <span>{added ? "CLAIMED" : "CLAIM PASS"}</span>
        </button>
      </div>
    </div>
  );
}
